import type { EraDef } from '../types';

/**
 * ERA 7 — MEDIEVAL
 * A castle siege at night. Siege engines shell you from outside your own range and
 * panic-shove backwards when you walk into their dead zone; rams and mounted
 * champions run fixed lanes straight through the courtyard; wall-top wardens paint
 * lines of burning oil with gaps you have to commit to. Cold moonlit stone, tiled
 * courtyard, torchlight. Boss: The Dragon, which takes to the air on a wing-beat
 * telegraph, becomes untargetable, bombs the field with fire pots and slams back
 * down inside a blinking ring. Palette: slate/steel grey with cold moonlight.
 * Enemy bullets: hot bolt-red (contrasts the cool player bullets). Hazards:
 * burning-oil orange — the era's hazard, painted in LINES with gaps rather than
 * scattered pools (era 5/6's trick), plus one trailing enemy (the Battering Ram).
 * Signature behaviors owned here: `siege`, `ram`. Boss special owned here: `flight`.
 */
const ENEMY_BULLET = 0xff3a2a;
const HAZARD = 0xffa028;

export const ERA_MEDIEVAL: EraDef = {
  index: 6,
  id: 'medieval',
  name: 'Medieval',
  subtitle: '1200 AD',
  implemented: true,
  palette: {
    sky: 0x16171c,
    fog: 0x24262d,
    fogDensity: 0.021,
    ground: 0x5a5f66,
    groundAlt: 0x474b52,
    ambient: 0x6e7684,
    sun: 0xcfd8e6,
    sunIntensity: 1.15,
    accent: 0x9fd8ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'castle',
    arenaRadius: 68,
    groundStyle: 'tiles',
    props: [
      { visual: { geo: 'box', color: 0x474b52, emissive: 0x14161a, emissiveIntensity: 0.15, scaleY: 4.5, anim: 'none' }, count: 20, scale: [0.7, 2.2], y: 0 },
      { visual: { geo: 'cone', color: 0xffa028, emissive: 0xffa028, emissiveIntensity: 0.9, anim: 'pulse' }, count: 14, scale: [0.3, 0.7], y: 0.5 },
      { visual: { geo: 'capsule', color: 0x3c3f45, scaleY: 2.6, anim: 'none' }, count: 14, scale: [0.4, 0.9], y: 0.4 },
    ],
  },
  enemies: [
    {
      id: 'm7_levy',
      name: 'Peasant Levy',
      archetype: 'swarmer',
      hp: 32,
      speed: 4.2,
      damage: 11,
      radius: 0.42,
      xp: 8,
      visual: { geo: 'capsule', color: 0x6a6e74, emissive: 0x2a2e32, emissiveIntensity: 0.3, scale: 0.95, anim: 'bob' },
      behavior: { type: 'chase' },
      // Filler: conscripts run straight at you in mobs. Counterplay: cheap kills, just don't let
      // them stack contact hits while you are threading a fire line or reading a ram's lane.
    },
    {
      id: 'm7_crossbow',
      name: 'Crossbow Knight',
      archetype: 'shooter',
      hp: 50,
      speed: 3.4,
      damage: 12,
      radius: 0.52,
      xp: 14,
      visual: { geo: 'box', color: 0x5a5e64, emissive: 0x2a2e32, emissiveIntensity: 0.4, scale: 1, scaleY: 1.15, anim: 'none' },
      behavior: { type: 'keepDistance', distance: 11, strafe: 0.28 },
      patterns: ['m7_bolt_volley'],
      patternCooldown: 2.6,
      fireRange: 17,
      // Filler ranged kiter: three very fast flat bolts, then a long reload (the 2.6 s cooldown IS the
      // reload). Counterplay: the bolts are near-instant, so break line on the volley and punish the reload.
    },
    {
      id: 'm7_warden',
      name: 'Oil Warden',
      archetype: 'shooter',
      hp: 76,
      speed: 2.9,
      damage: 11,
      radius: 0.55,
      xp: 16,
      shardChance: 0.1,
      visual: { geo: 'torus', color: 0xa85a28, emissive: 0xffa028, emissiveIntensity: 0.8, scale: 1, anim: 'spin' },
      behavior: { type: 'keepDistance', distance: 13, strafe: 0.15 },
      patterns: ['m7_fire_line'],
      patternCooldown: 2.8,
      fireRange: 18,
      // The era's hazard author: pours a gapped LINE of burning oil across the ground ~6 u in front of
      // itself, twice, with the holes in different places each time. Counterplay: pick your gap before the
      // line lands and commit — the wall is between you and the warden, so hesitating costs you the lane.
    },
    {
      id: 'm7_mangonel',
      name: 'Mangonel Crew',
      archetype: 'shooter',
      hp: 84,
      speed: 3.0,
      damage: 12,
      radius: 0.6,
      xp: 18,
      shardChance: 0.1,
      visual: { geo: 'box', color: 0x6a5236, emissive: 0x2a1c0e, emissiveIntensity: 0.3, scale: 1.15, scaleY: 0.85, anim: 'none' },
      behavior: { type: 'siege', minRange: 7, settleRange: 14, settleTime: 1.0, backSpeedMult: 0.8 },
      patterns: ['m7_mangonel_scatter'],
      patternCooldown: 2.4,
      fireRange: 18,
      // Signature: the light engine. Rolls to 14 u, plants (flash), then lobs oil pots in random directions
      // that pool where they land — it walls ITSELF in fire. Counterplay: it cannot shoot inside 7 u and
      // backs off if you get there, so dash through its own pools and kill it in the dead zone.
    },
    {
      id: 'm7_trebuchet',
      name: 'Siege Trebuchet',
      archetype: 'tank',
      hp: 120,
      speed: 2.4,
      damage: 14,
      radius: 0.78,
      xp: 24,
      shardChance: 0.14,
      visual: { geo: 'box', color: 0x5a4630, emissive: 0x241a0c, emissiveIntensity: 0.35, scale: 1.45, scaleY: 1.3, anim: 'none' },
      behavior: { type: 'siege', minRange: 10, settleRange: 19, settleTime: 1.5, backSpeedMult: 0.6 },
      patterns: ['m7_trebuchet_stone'],
      patternCooldown: 2.8,
      fireRange: 22,
      // Signature: the heavy engine. Shells you from 10–22 u — outside your 16 u weapon range at the top of
      // its band — with a slow stone that airbursts into shrapnel and leaves a burning pool. Counterplay:
      // walk INTO it. Inside 10 u it stops firing and shoves backwards, and its long settle flash is a free
      // burst window.
    },
    {
      id: 'm7_ram',
      name: 'Battering Ram',
      archetype: 'tank',
      hp: 140,
      speed: 2.3,
      damage: 24,
      radius: 0.95,
      xp: 28,
      shardChance: 0.15,
      visual: { geo: 'box', color: 0x4a3a26, emissive: 0xffa028, emissiveIntensity: 0.35, scale: 1.5, scaleY: 0.9, anim: 'none' },
      behavior: { type: 'ram', aimTime: 1.1, windup: 0.9, rampPerSec: 1.0, maxSpeedMult: 2.2, runTime: 2.6 },
      trail: { radius: 0.9, duration: 3, dps: 7, interval: 0.3, color: HAZARD },
      // Signature: a lane weapon with no patterns at all — it aims, heaves (0.9 s dead-still red pulse),
      // then rolls in a straight line with zero steering, accelerating to ~2.2x and crossing the arena while
      // its burning pitch roof paints the lane behind it. Counterplay: sidestep the lane during the heave;
      // never try to outrun it forwards, and remember the lane stays on fire after it passes.
    },
    {
      id: 'm7_champion',
      name: 'Mounted Champion',
      archetype: 'elite',
      elite: true,
      hp: 75,
      speed: 3.2,
      damage: 15,
      radius: 0.68,
      xp: 30,
      shardChance: 0.4,
      visual: { geo: 'cone', color: 0xd8dce2, emissive: 0xff3a2a, emissiveIntensity: 0.7, scale: 1.35, anim: 'none' },
      behavior: { type: 'ram', aimTime: 0.8, windup: 0.55, rampPerSec: 1.8, maxSpeedMult: 2.2, runTime: 1.6 },
      patterns: ['m7_champion_lance'],
      patternCooldown: 2.0,
      fireRange: 8,
      // Elite-tier lane charger: a couched lance run — shorter aim, snappier heave, much faster ramp, and it
      // sprays a wide needle fan as it passes. Counterplay: same read-the-heave rhythm as the ram, but the
      // window is half as long and you have to clear the fan as well as the body.
    },
  ],
  patterns: [
    {
      id: 'm7_bolt_volley',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.18, bursts: 3, count: 1, spread: 0.05,
          bullet: { speed: 13, lifetime: 2.0, radius: 0.2, damage: 14, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'm7_fire_line',
      emitters: [
        { shape: 'wall', start: 0, interval: 1.5, bursts: 2, count: 8, spread: 14, gaps: 2,
          bullet: { speed: 5, accel: -1.8, minSpeed: 1.2, lifetime: 1.6, radius: 0.32, damage: 13, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.1, duration: 4.5, dps: 11, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_trebuchet_stone',
      emitters: [
        { shape: 'aimed', interval: 3.0, bursts: 2, count: 1,
          bullet: { speed: 6, accel: -1.2, minSpeed: 1.5, lifetime: 3.2, radius: 0.5, damage: 28, visual: 'blob', color: HAZARD,
                    onExpire: 'm7_stone_airburst',
                    linger: { radius: 1.9, duration: 4, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_stone_airburst',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 12,
          bullet: { speed: 8, lifetime: 0.8, radius: 0.22, damage: 12, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'm7_mangonel_scatter',
      emitters: [
        { shape: 'random', interval: 1.5, bursts: 3, count: 2, spread: 6.283,
          bullet: { speed: 4.5, accel: -1.0, minSpeed: 1.0, lifetime: 2.4, radius: 0.36, damage: 22, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.2, duration: 3.5, dps: 10, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_champion_lance',
      emitters: [
        { shape: 'aimed', interval: 0.45, bursts: 2, count: 5, spread: 1.2,
          bullet: { speed: 9, lifetime: 1.1, radius: 0.24, damage: 16, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'm7_boss_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 14,
          bullet: { speed: 6.5, lifetime: 1.8, radius: 0.34, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'm7_boss_breath',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.32, bursts: 6, count: 5, spread: 0.5,
          bullet: { speed: 7, accel: -1.8, minSpeed: 1.2, lifetime: 1.5, radius: 0.34, damage: 15, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.0, duration: 3, dps: 10, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_boss_wing_blast',
      emitters: [
        { shape: 'radial', start: 0, interval: 1.1, bursts: 2, count: 18,
          bullet: { speed: 7.5, lifetime: 2.0, radius: 0.28, damage: 16, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'radial', start: 0.55, interval: 1.1, bursts: 2, count: 18, angle: 0.175,
          bullet: { speed: 5.5, lifetime: 2.4, radius: 0.28, damage: 14, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'm7_boss_firepot',
      emitters: [
        { shape: 'random', interval: 1, bursts: 1, count: 3, spread: 6.283,
          bullet: { speed: 3.2, accel: -1.2, minSpeed: 0.8, lifetime: 1.6, radius: 0.42, damage: 20, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.6, duration: 4, dps: 13, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_boss_slam',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 16,
          bullet: { speed: 6.5, lifetime: 1.5, radius: 0.36, damage: 18, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'm7_boss_slam_heavy',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20,
          bullet: { speed: 7.5, lifetime: 1.6, radius: 0.34, damage: 18, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 20, angle: 0.157,
          bullet: { speed: 5, lifetime: 2.2, radius: 0.34, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'random', interval: 1, bursts: 1, count: 4, spread: 6.283,
          bullet: { speed: 3.4, accel: -1.2, minSpeed: 0.8, lifetime: 1.6, radius: 0.42, damage: 20, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.5, duration: 4, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_boss_inferno_lines',
      emitters: [
        { shape: 'wall', start: 0, interval: 2.2, bursts: 2, count: 9, spread: 16, gaps: 2,
          bullet: { speed: 5.5, accel: -1.8, minSpeed: 1.2, lifetime: 1.8, radius: 0.34, damage: 15, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.2, duration: 4.5, dps: 12, color: HAZARD } } },
        { shape: 'wall', start: 0.7, interval: 2.2, bursts: 2, count: 9, spread: 16, gaps: 2, angle: 1.05,
          bullet: { speed: 5.0, accel: -1.8, minSpeed: 1.2, lifetime: 2.0, radius: 0.34, damage: 15, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.2, duration: 4.5, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 'm7_boss_phase3_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 22,
          bullet: { speed: 7, lifetime: 2.0, radius: 0.32, damage: 18, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 14, angle: 0.22,
          bullet: { speed: 4.2, lifetime: 2.6, radius: 0.4, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'wall', interval: 1, bursts: 1, count: 9, spread: 18, gaps: 3,
          bullet: { speed: 5.5, accel: -1.8, minSpeed: 1.2, lifetime: 1.8, radius: 0.34, damage: 16, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.2, duration: 5, dps: 12, color: HAZARD } } },
      ],
    },
  ],
  boss: {
    id: 'm7_dragon',
    name: 'The Dragon',
    title: 'Terror of the Siege',
    // Balance: 3900 base HP scales to ~34,100 effective at the era-7 boss stage multiplier (8.74x); the boss is untargetable ~18-27% of each phase while airborne, giving a ~64-79s fight versus era 6's ~60-75s baseline. Tune interval/airTime before HP if timing feels off.
    hp: 3900,
    radius: 2.6,
    speed: 2.0,
    damage: 36,
    xp: 560,
    visual: { geo: 'spike', color: 0x7a1c26, emissive: 0xff4a20, emissiveIntensity: 0.8, scale: 1.5, anim: 'bob' },
    phases: [
      {
        hpBelow: 1,
        name: 'Wheeling',
        behavior: { type: 'keepDistance', distance: 9, strafe: 0.22 },
        patterns: ['m7_boss_breath', 'm7_boss_wing_blast'],
        patternCooldown: 2.2,
        transition: 1.5,
        onEnter: 'm7_boss_enter',
        special: { type: 'flight', interval: 15, windup: 1.0, height: 3.2, airTime: 2.4,
                   bombInterval: 1.1, bombPattern: 'm7_boss_firepot',
                   landPattern: 'm7_boss_slam', landRange: 5,
                   landTelegraph: 0.55, landRadius: 3.0, landDps: 12 },
      },
      {
        hpBelow: 0.66,
        name: 'Wings of Ash',
        behavior: { type: 'chase' },
        speedMult: 1.15,
        patterns: ['m7_boss_breath', 'm7_boss_inferno_lines'],
        patternCooldown: 1.9,
        transition: 2,
        onEnter: 'm7_boss_inferno_lines',
        special: { type: 'flight', interval: 13, windup: 0.85, height: 3.6, airTime: 3.0,
                   bombInterval: 0.9, bombPattern: 'm7_boss_firepot',
                   landPattern: 'm7_boss_slam', landRange: 4,
                   landTelegraph: 0.5, landRadius: 3.2, landDps: 13 },
      },
      {
        hpBelow: 0.32,
        name: 'Terror of the Siege',
        behavior: { type: 'orbit', radius: 6.5, angular: 0.9 },
        speedMult: 1.25,
        patterns: ['m7_boss_inferno_lines', 'm7_boss_wing_blast', 'm7_boss_breath'],
        patternCooldown: 1.5,
        transition: 2,
        onEnter: 'm7_boss_phase3_enter',
        special: { type: 'flight', interval: 11, windup: 0.7, height: 4.0, airTime: 2.6,
                   bombInterval: 0.7, bombPattern: 'm7_boss_firepot',
                   landPattern: 'm7_boss_slam_heavy', landRange: 3,
                   landTelegraph: 0.45, landRadius: 3.5, landDps: 14 },
      },
    ],
  },
  stages: [
    {
      name: 'The Outer Bailey',
      duration: 220,
      objective: { type: 'defend', seconds: 160, health: 120, radius: 7, label: 'Defend the Outer Bailey' },
      density: { start: 1.1, end: 2.8 },
      pool: [
        { enemy: 'm7_levy', weight: 10 },
        { enemy: 'm7_crossbow', weight: 4, minTime: 20 },
        { enemy: 'm7_warden', weight: 3, minTime: 40 },
        { enemy: 'm7_mangonel', weight: 2, minTime: 55 },
        { enemy: 'm7_trebuchet', weight: 1, minTime: 80 },
        { enemy: 'm7_ram', weight: 1, minTime: 110 },
      ],
      waves: [
        { t: 5, enemy: 'm7_levy', count: 5, formation: 'ring' },
        { t: 24, enemy: 'm7_crossbow', count: 3, formation: 'line' },
        { t: 48, enemy: 'm7_levy', count: 16, formation: 'cluster' },
        { t: 70, enemy: 'm7_warden', count: 2, formation: 'edge' },
        { t: 95, enemy: 'm7_mangonel', count: 2, formation: 'line' },
        { t: 120, enemy: 'm7_ram', count: 1, formation: 'edge' },
        { t: 150, enemy: 'm7_crossbow', count: 5, formation: 'random' },
        { t: 175, enemy: 'm7_levy', count: 20, formation: 'ring' },
        { t: 198, enemy: 'm7_champion', count: 1, formation: 'edge' },
      ],
    },
    {
      name: 'Breach the Curtain Wall',
      duration: 250,
      objective: { type: 'hunt', enemy: 'm7_trebuchet', count: 18, label: 'Destroy the siege engines' },
      density: { start: 1.5, end: 3.4 },
      pool: [
        { enemy: 'm7_levy', weight: 8 },
        { enemy: 'm7_crossbow', weight: 5 },
        { enemy: 'm7_warden', weight: 4 },
        { enemy: 'm7_mangonel', weight: 4 },
        { enemy: 'm7_trebuchet', weight: 3, minTime: 30 },
        { enemy: 'm7_ram', weight: 2, minTime: 50 },
        { enemy: 'm7_champion', weight: 1, minTime: 90 },
      ],
      waves: [
        { t: 5, enemy: 'm7_crossbow', count: 5, formation: 'ring' },
        { t: 35, enemy: 'm7_trebuchet', count: 2, formation: 'edge' },
        { t: 65, enemy: 'm7_warden', count: 4, formation: 'ring' },
        { t: 95, enemy: 'm7_ram', count: 2, formation: 'line' },
        { t: 120, enemy: 'm7_champion', count: 1, formation: 'edge' },
        { t: 145, enemy: 'm7_levy', count: 30, formation: 'ring' },
        { t: 175, enemy: 'm7_mangonel', count: 3, formation: 'random' },
        { t: 205, enemy: 'm7_trebuchet', count: 3, formation: 'cluster' },
        { t: 230, enemy: 'm7_champion', count: 2, formation: 'line' },
      ],
    },
    {
      name: "The Dragon's Keep",
      duration: 600,
      density: { start: 1.0, end: 1.0 },
      pool: [
        { enemy: 'm7_levy', weight: 6 },
        { enemy: 'm7_crossbow', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'm7_levy', count: 10, formation: 'ring' }],
      boss: { id: 'm7_dragon', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_dragonmail', weight: 2 },
    { itemId: 'r_siege_crank', weight: 2 },
    { itemId: 'w_crossbow', weight: 2 },
    { itemId: 'w_bone_spear', weight: 1 },
    { itemId: 'a_chitin', weight: 2 },
    { itemId: 'a_hide', weight: 1 },
    { itemId: 'r_amber', weight: 1 },
    { itemId: 'r_trilobite_eye', weight: 1 },
  ],
  audio: { music: 'music/era7_medieval', ambient: 'amb/castle', sfxSet: 'siege' },
};
